<script setup lang="ts">
/**
 * Right settings pane. Floats over the score as an absolutely-positioned
 * overlay (see GameArea's .settings-pane-overlay) — width is what animates,
 * and the pane stays mounted at width 0 and `inert` while closed, which keeps
 * its controls out of the tab order without breaking the transition.
 *
 * Closes on: the toolbar gear (toggling `showSettingsPane` from outside),
 * a pointerdown anywhere outside the pane and its own gear toggle (mirrors
 * MainNav's mobile-menu outside-click), or a right-ward drag past a distance
 * threshold (mirrors MainNav's mobile-menu drag-to-dismiss, mirrored in
 * direction since this pane is anchored to the right edge instead of the
 * left). See useEdgeDragDismiss / useOutsidePointerDismiss for the shared
 * mechanics.
 */
import { useSettings } from '@/composables/useSettings';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { useEdgeDragDismiss } from '@/composables/useEdgeDragDismiss';
import { useOutsidePointerDismiss } from '@/composables/useOutsidePointerDismiss';
import QuickSettingsRow from '@/components/shared/QuickSettingsRow.vue';
import GameAreaPreviewModeRow from '@/components/GameAreaPreviewModeRow.vue';
import GameAreaTargetLineRow from '@/components/GameAreaTargetLineRow.vue';

defineProps<{
  /** Speed and preview mode are host-controlled, so participants don't get them. */
  showPlaybackSettings?: boolean;
}>();

const open = defineModel<boolean>('open', { required: true });
defineEmits<{ (e: 'pause'): void }>();

const { zoom, audioVolume, metronomeVolume } = useSettings();
const gameEngine = useInjectGameEngine();

const { dragTranslateX, isDragging, handlers: dragHandlers } = useEdgeDragDismiss(open, {
  anchor: 'right',
  // The sliders in here (zoom/volume/…) are native <input type="range"> —
  // horizontal drags of their own. A dismiss-drag starting on one would
  // fight the slider for the same gesture, so those (and any other control)
  // opt out.
  isInteractiveTarget: (target) =>
    target instanceof HTMLElement &&
    !!target.closest('input, button, select, textarea, [role="slider"]'),
});

// Click-outside dismiss: any pointerdown outside the pane itself and outside
// its own gear toggle (GameControlBar's .settings-toggle-button — excluded so
// that click isn't double-handled) closes it.
useOutsidePointerDismiss(open, ['.settings-pane', '.settings-toggle-button']);
</script>

<template>
  <div
    class="settings-pane"
    :class="{ 'settings-pane--open': open, 'settings-pane--dragging': isDragging }"
    :style="{ transform: dragTranslateX ? `translateX(${dragTranslateX}px)` : undefined }"
    :inert="!open"
    role="group"
    aria-label="Game settings"
    v-on="dragHandlers"
  >
    <div class="settings-pane-content">
      <template v-if="showPlaybackSettings">
        <section class="settings-section">
          <QuickSettingsRow
            v-model="gameEngine.playbackSpeed.value"
            label="Speed"
            :min="0.5"
            :max="1"
            :step="0.05"
            :disabled="gameEngine.isPlaying.value"
            slider-aria-label="Playback speed"
          />
          <!-- Fixed cell: both hints can be true at once (slowed and playing),
               and neither may push the rows below it. -->
          <div class="settings-hint-slot">
            <p
              v-if="gameEngine.isPlaying.value"
              class="settings-hint"
            >
              Pause to change speed.
            </p>
            <!-- TODO: when a personal-best / high-score feature lands, runs at
                 playbackSpeed !== 1 must NOT be considered as best-score
                 candidates. Mirror this UI condition in the score-saving path
                 (likely in useGameEngine or wherever scores are persisted). -->
            <p
              v-if="gameEngine.playbackSpeed.value !== 1"
              class="settings-hint"
            >
              Score is not recorded at slowed tempo.
            </p>
          </div>

          <GameAreaTargetLineRow />
          <GameAreaPreviewModeRow @pause="$emit('pause')" />
        </section>

        <hr class="settings-divider">
      </template>

      <section class="settings-section">
        <QuickSettingsRow
          v-model="zoom"
          label="Zoom"
          :min="0.5"
          :max="4.0"
          :step="0.1"
          slider-aria-label="Zoom level"
        />
        <QuickSettingsRow
          v-model="audioVolume"
          label="Music"
          :min="0"
          :max="1"
          :step="0.01"
          slider-aria-label="Music volume"
        />
        <QuickSettingsRow
          v-model="metronomeVolume"
          label="Metronome"
          :min="0"
          :max="1"
          :step="0.01"
          slider-aria-label="Metronome volume"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
.settings-pane {
  width: 0;
  height: 100%;
  overflow: hidden;
  background: var(--color-secondary);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.35);
  transition: width 0.22s ease, transform 0.2s ease;
}

.settings-pane--open {
  width: 350px;
}

/* No transition while actively dragging, so the pane tracks the finger with
   zero lag — mirrors MainNav's mobile-menu-container--dragging. Width never
   actually changes mid-drag (it only flips on open/close, which happens
   after isDragging is already back to false), so dropping both properties
   here is equivalent to only dropping transform, just simpler. */
.settings-pane--dragging {
  transition: none;
}

/* Fixed width so the rows inside don't reflow while the pane animates. */
.settings-pane-content {
  width: 350px;
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-divider {
  border: none;
  border-top: 1px solid var(--color-surface-hover);
  margin: 0;
}

/* Worst case is both hints wrapping: 1 line + 2 lines at 13px/1.4 plus the gap. */
.settings-hint-slot {
  min-height: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings-hint {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--color-text-tertiary, var(--color-text-secondary));
  font-style: italic;
}
</style>
